import {
  CountGroupIssue,
  CountGroupToday,
  Issue,
  Reason,
  Task,
  TaskData,
  TaskForm,
} from "../types/task";
import { authFetcher } from "./../utils/auth-fetch";

const getTask = async (id: string) => {
  return await authFetcher<Task>("GET", `tasks/${id}`);
};

const getTasks = async (params: Record<string, any> = {}) => {
  return await authFetcher<TaskData>("GET", "tasks", null, {
    ...params,
  });
};

const getTaskCsvFilename = (params: { start_date: string; end_date: string }) => {
  const dateRange =
    params.start_date === params.end_date
      ? params.start_date
      : `${params.start_date}_to_${params.end_date}`;
  return `essilor-tasks_${dateRange}.csv`;
};

const getFilenameFromContentDisposition = (
  contentDisposition: string | undefined,
  fallbackFilename: string,
) => {
  const filenameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i,
  );
  const filename = filenameMatch?.[1]
    ? decodeURIComponent(filenameMatch[1])
    : fallbackFilename;
  return filename.toLowerCase().endsWith(".csv") ? filename : `${filename}.csv`;
};

const downloadTasksCsv = async (params: {
  start_date: string;
  end_date: string;
}) => {
  const response = await authFetcher<Blob>(
    "GET",
    "/download/tasks",
    null,
    params,
    null,
    false,
    undefined,
    undefined,
    "blob",
  );

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const filename = getFilenameFromContentDisposition(
    response.headers?.["content-disposition"],
    getTaskCsvFilename(params),
  );
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getSupportTasks = async (
  status: "pending" | "in-progress" | "complete",
) => {
  return await authFetcher<Task[]>("GET", `tasks/support/${status}`);
};

const getYourTasks = async (role: "task-creator" | "key-in" | "qc") => {
  return await authFetcher<Task[]>("GET", `owner-tasks/${role}/urgent`);
};

const createTask = async (data: TaskForm) => {
  return await authFetcher<Task>("POST", "tasks/normal", data);
};

const createUrgentTask = async (data: TaskForm) => {
  return await authFetcher<Task>("POST", "tasks/urgent", data);
};

const updateTask = async (id: string, data: TaskForm) => {
  return await authFetcher<Task>("PATCH", `tasks/${id}`, data);
};

const deleteTask = async (id: string) => {
  return await authFetcher<any>("DELETE", `tasks/${id}`);
};

const keyinGetTasks = async () => {
  return await authFetcher<Task[]>("GET", "owner-tasks/key-in/normal");
};

const keyinCallJob = async (data: { type: string; limit: number }) => {
  return await authFetcher<TaskData>(
    "POST",
    "owner-tasks/key-in/call-job",
    data,
  );
};

const qcGetTasks = async () => {
  return await authFetcher<Task[]>("GET", "owner-tasks/qc/normal");
};

const qcCallJob = async (data: { type: string; limit: number }) => {
  return await authFetcher<TaskData>("POST", "owner-tasks/qc/call-job", data);
};

const returnJob = async (data: { task_id: string; note: string }) => {
  return await authFetcher<TaskData>("POST", "owner-tasks/return-job", data);
};

const keyinComplete = async (
  taskId: string,
  data: { tracking_numbers: string[] },
) => {
  return await authFetcher<any>(
    "POST",
    `tasks/key-in/${taskId}/complete`,
    data,
  );
};

const keyinIssue = async (
  taskId: string,
  data: {
    issue: string;
    tracking_numbers: string[] | undefined;
    details: string;
  },
) => {
  return await authFetcher<any>("POST", `tasks/key-in/${taskId}/issue`, data);
};

const getIssues = async () => {
  return await authFetcher<Issue[]>("GET", "tasks/common-data/issue-list");
};

const getRejectReasons = async () => {
  return await authFetcher<Reason[]>(
    "GET",
    "tasks/common-data/reject-reason-list",
  );
};

const supportCancel = async (taskId: string) => {
  return await authFetcher<any>("POST", `/tasks/support/${taskId}/cancel`);
};

const qcComplete = async (taskId: string) => {
  return await authFetcher<any>("POST", `tasks/qc/${taskId}/complete`);
};

const qcReject = async (taskId: string, data: { reason: string }) => {
  return await authFetcher<any>("POST", `tasks/qc/${taskId}/reject`, data);
};

const countGroupBySupportIssues = async (type = "normal") => {
  return await authFetcher<CountGroupIssue[]>(
    "GET",
    `tasks/support/count-group-by-issue-value/${type}`,
  );
};

const countTodayGroupByStatus = async (
  taskUrgent = false,
  taskStatus: "support" | "qc" = "support",
) => {
  return await authFetcher<CountGroupToday>(
    "GET",
    `tasks/${taskStatus}/count-today-group-by-${taskStatus}-status/${taskUrgent ? "urgent" : "normal"}`,
  );
};

export {
  countGroupBySupportIssues,
  countTodayGroupByStatus,
  createTask,
  createUrgentTask,
  deleteTask,
  downloadTasksCsv,
  getIssues,
  getRejectReasons,
  getSupportTasks,
  getTask,
  getTasks,
  getYourTasks,
  keyinCallJob,
  keyinComplete,
  keyinGetTasks,
  keyinIssue,
  qcCallJob,
  qcComplete,
  qcGetTasks,
  qcReject,
  returnJob,
  supportCancel,
  updateTask,
};
